export type Mood =
  | "heartbroken"
  | "obsessed"
  | "angry"
  | "healing"
  | "forbidden"
  | "second-chance"
  | "comfort";

export type Craving =
  | "chosen"
  | "intensity-longing"
  | "regret"
  | "obsession-tension"
  | "slow-destruction"
  | "safety-after-chaos"
  | "forbidden-attraction"
  | "emotional-payoff";

export type SpiceLevel = 1 | 2 | 3; // 1 closed door, 2 medium, 3 explicit
export type Intensity = "light" | "emotional" | "devastating";

export interface Book {
  id: string;
  title: string;
  author: string;
  hook: string;
  tropes: string[];
  spiceLevel: SpiceLevel;
  emotionalIntensity: Intensity;
  mood: Mood[];
  cravings: Craving[];
  triggerWarnings?: string[];
  coverColor: string;
  coverImageUrl?: string;
  featured?: boolean;
  readUrl?: string;
  ebookAffiliateLink?: string | null;
  physicalBookAffiliateLink?: string | null;
  audiobookAffiliateLink?: string | null;
  hasAudiobook?: boolean;
  subgenre?: string[];
  representation?: string[];
  representationCustom?: string | null;
  seriesType?: string | null;
  readingExperience?: string | null;
  createdAt?: string | null;
}

export const books: Book[] = [
  {
    id: "1",
    title: "The Way He Burns",
    author: "Elena Marsh",
    hook: "He didn't just lose her. He destroyed her. Now he has to earn her back—one shattered piece at a time.",
    tropes: ["Second chance romance", "Grovel / redemption arc", "Alpha / dominant hero", "Forced proximity"],
    spiceLevel: 3,
    emotionalIntensity: "devastating",
    mood: ["heartbroken", "second-chance", "obsessed"],
    cravings: ["regret", "slow-destruction", "emotional-payoff"],
    triggerWarnings: ["Past trauma", "Emotional manipulation"],
    coverColor: "from-rose-900 to-red-950",
    featured: true,
  },
  {
    id: "2",
    title: "Whispers After Midnight",
    author: "Ava Sinclair",
    hook: "She swore she'd never go back. But his voice on the phone at 2am? That undid everything.",
    tropes: ["Forbidden love", "Second chance romance", "Secret relationship", "One bed"],
    spiceLevel: 2,
    emotionalIntensity: "emotional",
    mood: ["forbidden", "healing", "second-chance"],
    cravings: ["intensity-longing", "forbidden-attraction"],
    coverColor: "from-purple-900 to-indigo-950",
    featured: true,
  },
  {
    id: "3",
    title: "Broken Vows",
    author: "Mia Drake",
    hook: "Their marriage was a contract. But the way he looks at her? That's not in the fine print.",
    tropes: ["Arranged marriage", "Enemies to lovers", "Alpha / dominant hero", "Forced proximity"],
    spiceLevel: 3,
    emotionalIntensity: "emotional",
    mood: ["angry", "obsessed", "forbidden"],
    cravings: ["obsession-tension", "chosen"],
    coverColor: "from-amber-900 to-orange-950",
    featured: true,
  },
  {
    id: "4",
    title: "After We Fell Apart",
    author: "Nina Cole",
    hook: "This book will ruin you—in the best way. You'll cry. You'll scream. You'll read it again.",
    tropes: ["Second chance romance", "Single parent", "Small town romance", "Grovel / redemption arc"],
    spiceLevel: 1,
    emotionalIntensity: "devastating",
    mood: ["heartbroken", "healing", "second-chance"],
    cravings: ["regret", "emotional-payoff", "slow-destruction"],
    coverColor: "from-teal-900 to-emerald-950",
    featured: true,
  },
  {
    id: "5",
    title: "His Obsession",
    author: "Jade Rivera",
    hook: "He watched her from the shadows. Not because he was dangerous—because he was hers. And she didn't know it yet.",
    tropes: ["Possessive / obsessive hero", "Alpha / dominant hero", "Forbidden love", "Age gap"],
    spiceLevel: 3,
    emotionalIntensity: "emotional",
    mood: ["obsessed", "forbidden", "angry"],
    cravings: ["obsession-tension", "forbidden-attraction", "chosen"],
    coverColor: "from-slate-800 to-gray-950",
  },
  {
    id: "6",
    title: "Sunflower Season",
    author: "Lily Park",
    hook: "She came to the farm to forget. He made her remember what it felt like to be wanted.",
    tropes: ["Small town romance", "Grumpy / sunshine", "Friends to lovers"],
    spiceLevel: 1,
    emotionalIntensity: "light",
    mood: ["healing", "comfort", "second-chance"],
    cravings: ["safety-after-chaos", "chosen"],
    coverColor: "from-yellow-800 to-amber-950",
  },
];

export const moods: { id: Mood; label: string }[] = [
  { id: "heartbroken", label: "💔 Heartbroken" },
  { id: "obsessed", label: "🔥 Obsessed" },
  { id: "angry", label: "😤 Angry / Revenge" },
  { id: "healing", label: "🌿 Healing" },
  { id: "forbidden", label: "🚫 Forbidden Desire" },
  { id: "second-chance", label: "🔄 Second Chance Love" },
  { id: "comfort", label: "🍯 Comfort / Soft Romance" },
];

export const cravings: { id: Craving; label: string }[] = [
  { id: "chosen", label: "I want to feel chosen" },
  { id: "intensity-longing", label: "Emotional intensity and longing" },
  { id: "regret", label: "A man who regrets losing her" },
  { id: "obsession-tension", label: "Obsession and tension" },
  { id: "slow-destruction", label: "Slow emotional destruction leading to love" },
  { id: "safety-after-chaos", label: "Safety after emotional chaos" },
  { id: "forbidden-attraction", label: "Forbidden attraction I shouldn't root for" },
  { id: "emotional-payoff", label: "A powerful emotional payoff at the end" },
];

export const tropeGroups: { group: string; tropes: string[] }[] = [
  {
    group: "Core romance tropes",
    tropes: [
      "Second chance romance",
      "Enemies to lovers",
      "Friends to lovers",
      "Fake dating",
      "Forced proximity",
      "One bed",
      "Arranged marriage",
      "Marriage of convenience",
      "Secret relationship",
      "Hidden identity",
    ],
  },
  {
    group: "High emotion arcs",
    tropes: [
      "Grovel / redemption arc",
      "Forbidden love",
      "Age gap",
      "Single parent",
      "Grumpy / sunshine",
    ],
  },
  {
    group: "Character dynamics",
    tropes: ["Alpha / dominant hero", "Possessive / obsessive hero"],
  },
  {
    group: "Settings",
    tropes: ["Small town romance"],
  },
];

export const allTropes = tropeGroups.flatMap((g) => g.tropes);

export const spiceOptions: { value: SpiceLevel; label: string }[] = [
  { value: 1, label: "🌸 Closed door (fade to black)" },
  { value: 2, label: "🔥 Medium heat" },
  { value: 3, label: "🌶️ Explicit / open door" },
];

export const intensityOptions: { value: Intensity; label: string }[] = [
  { value: "light", label: "☀️ Light / Comfort read" },
  { value: "emotional", label: "💧 Emotional / Heavy feelings" },
  { value: "devastating", label: "💀 Devastating / Emotional wreck" },
];
